import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Select, message, Button } from 'antd';
import { YoutubeOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { apiClient } from '../services/api';

const { Option } = Select;

const VideoSubmissionModal = ({ visible, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hashtags, setHashtags] = useState([]);

  const loadHashtags = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/hashtags');
      if (response.data.status === 'success') {
        const activeHashtags = response.data.data.filter(h => h.is_active);
        setHashtags(activeHashtags);

        // Set default hashtag
        if (activeHashtags.length > 0) {
          form.setFieldsValue({ hashtag: activeHashtags[0].text });
        }
      }
    } catch (error) {
      console.error('Failed to load hashtags:', error);
    }
  }, [form]);

  // Load hashtags on mount
  useEffect(() => {
    if (visible) {
      loadHashtags();
    }
  }, [visible, loadHashtags]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/api/user/submit-video', {
        video_url: values.video_url,
        hashtag: values.hashtag,
        title: values.title
      });

      if (response.data.status === 'success') {
        message.success(response.data.message || 'Video berhasil didaftarkan!');
        form.resetFields();
        onSuccess && onSuccess(response.data.data);
        onClose();
      } else {
        message.error(response.data.message || 'Gagal mendaftarkan video');
      }
    } catch (error) {
      console.error('Video submission error:', error);
      const errorMsg = error.response?.data?.message || 'Terjadi kesalahan saat mendaftarkan video';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <VideoCameraOutlined style={{ fontSize: '20px', color: '#14B8A6' }} />
          <span>Submit Video Saya</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <p style={{ marginBottom: 24, color: '#666' }}>
        Daftarkan video YouTube atau blog Anda yang menggunakan AI untuk proses verifikasi dan penilaian.
      </p>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="URL Video"
          name="video_url"
          rules={[
            { required: true, message: 'URL video wajib diisi' },
            {
              pattern: /(youtube\.com|youtu\.be|blogspot\.com|blogger\.com|google\.com)/,
              message: 'URL harus dari YouTube atau Google (BlogSpot)'
            }
          ]}
        >
          <Input
            prefix={<YoutubeOutlined />}
            placeholder="https://youtube.com/watch?v=... atau https://..."
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Judul Video"
          name="title"
          rules={[
            { required: true, message: 'Judul video wajib diisi' },
            { max: 200, message: 'Judul maksimal 200 karakter' }
          ]}
        >
          <Input
            placeholder="Masukkan judul video Anda..."
            size="large"
            maxLength={200}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="Hashtag"
          name="hashtag"
          rules={[{ required: true, message: 'Hashtag wajib dipilih' }]}
        >
          <Select
            placeholder="Pilih hashtag"
            size="large"
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {hashtags.map(hashtag => (
              <Option key={hashtag.id} value={hashtag.text}>
                {hashtag.text} {hashtag.description && `- ${hashtag.description}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{
          padding: '12px',
          background: '#e6f7ff',
          border: '1px solid #91d5ff',
          borderRadius: '4px',
          marginBottom: 16
        }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            💡 Tips:
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: '13px' }}>
            <li>Pastikan video terkait dengan AI dan teknologi</li>
            <li>Gunakan hashtag yang sesuai (#AI-ITERA)</li>
            <li>Video akan diverifikasi oleh admin sebelum masuk leaderboard</li>
            <li>Anda bisa cek status verifikasi di halaman profil Anda</li>
          </ul>
        </div>

        <Form.Item style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>
              Batal
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Submit Video
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default VideoSubmissionModal;
